import React, { useState, useEffect } from "react";
import {
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  PersonRemove as RevokeIcon,
  ManageAccounts as ManageAccountsIcon,
  CheckCircle as ActiveIcon,
} from "@mui/icons-material";
import { Scorekeeper } from "../types";
import { apiRequest } from "../config/api";

function ManageScorekeepers() {
  const [scorekeepers, setScorekeepers] = useState<Scorekeeper[]>([]);
  const [filteredScorekeepers, setFilteredScorekeepers] = useState<
    Scorekeeper[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [revokeDialog, setRevokeDialog] = useState<{
    open: boolean;
    scorekeeperId: string;
    username: string;
  }>({ open: false, scorekeeperId: "", username: "" });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchScorekeepers();
  }, []);

  useEffect(() => {
    // Filter scorekeepers based on search term
    const filtered = scorekeepers.filter(
      (scorekeeper) =>
        scorekeeper.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        scorekeeper.firstName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        scorekeeper.lastName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredScorekeepers(filtered);
  }, [scorekeepers, searchTerm]);

  const fetchScorekeepers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await apiRequest("/api/admin/scorekeepers", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setScorekeepers(data);
      } else {
        console.error("Failed to fetch scorekeepers");
      }
    } catch (error) {
      console.error("Error fetching scorekeepers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = (scorekeeperId: string, username: string) => {
    setRevokeDialog({
      open: true,
      scorekeeperId,
      username,
    });
  };

  const confirmRevoke = async () => {
    const { scorekeeperId } = revokeDialog;

    try {
      setProcessing(scorekeeperId);
      const token = localStorage.getItem("authToken");
      const response = await apiRequest(
        `/api/admin/scorekeepers/${scorekeeperId}/revoke`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setSnackbar({
          open: true,
          message: "Scorekeeper access revoked successfully!",
          severity: "success",
        });
        await fetchScorekeepers(); // Refresh the list
      } else {
        const errorData = await response.json();
        setSnackbar({
          open: true,
          message: errorData.error || "Failed to revoke access",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error revoking access:", error);
      setSnackbar({
        open: true,
        message: "Network error while revoking access",
        severity: "error",
      });
    } finally {
      setProcessing(null);
      setRevokeDialog({ open: false, scorekeeperId: "", username: "" });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <ManageAccountsIcon sx={{ fontSize: 32, color: "primary.main" }} />
        <Typography variant="h4" fontWeight="bold">
          Manage Scorekeepers
        </Typography>
        {scorekeepers.length > 0 && (
          <Chip
            label={`${scorekeepers.length} active`}
            color="success"
            variant="filled"
          />
        )}
      </Box>

      {scorekeepers.length === 0 ? (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <ManageAccountsIcon
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Active Scorekeepers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Approved scorekeepers will appear here for management.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Search Bar */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <TextField
                fullWidth
                placeholder="Search by username, first name, or last name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </CardContent>
          </Card>

          {/* Scorekeepers Table */}
          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.light" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                    Username
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                    Full Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                    Approved Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", color: "white" }}
                    align="center"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredScorekeepers.map((scorekeeper) => (
                  <TableRow key={scorekeeper.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {scorekeeper.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {scorekeeper.firstName} {scorekeeper.lastName}
                    </TableCell>
                    <TableCell>
                      {scorekeeper.approvedAt
                        ? formatDate(scorekeeper.approvedAt.toString())
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="ACTIVE"
                        color="success"
                        variant="filled"
                        size="small"
                        icon={<ActiveIcon />}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<RevokeIcon />}
                        onClick={() =>
                          handleRevoke(scorekeeper.id, scorekeeper.username)
                        }
                        disabled={processing === scorekeeper.id}
                        sx={{ minWidth: 120 }}
                      >
                        Revoke Access
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredScorekeepers.length === 0 && searchTerm && (
            <Card sx={{ mt: 3, textAlign: "center", py: 4 }}>
              <CardContent>
                <SearchIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Results Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No scorekeepers match your search criteria: "{searchTerm}"
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={revokeDialog.open}
        onClose={() => setRevokeDialog((prev) => ({ ...prev, open: false }))}
      >
        <DialogTitle>Confirm Revoke Access</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revoke scorekeeper access for{" "}
            <strong>{revokeDialog.username}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will immediately prevent the user from accessing the scoring
            system. They can request access again later.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setRevokeDialog((prev) => ({ ...prev, open: false }))
            }
            disabled={processing !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRevoke}
            variant="contained"
            color="error"
            disabled={processing !== null}
          >
            {processing === revokeDialog.scorekeeperId
              ? "Revoking..."
              : "Revoke Access"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ManageScorekeepers;
